import { useMemo, useState } from 'react';
import { CATEGORIES, FEED_ITEMS } from '@/mocks/entertainment';
import SectionHeading from './SectionHeading';

interface TrendFeedProps {
  query: string;
}

function heatText(n: number): string {
  if (n >= 10000) return `${(n / 10000).toFixed(1)} 万`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)} 千`;
  return `${n}`;
}

const CAT_COLORS: Record<string, string> = {
  演员: 'bg-primary-500',
  歌手: 'bg-accent-500',
  爱豆: 'bg-secondary-500',
  综艺: 'bg-primary-700',
  电影: 'bg-accent-700',
  幕后: 'bg-foreground-600',
};

export default function TrendFeed({ query }: TrendFeedProps) {
  const [cat, setCat] = useState('全部');
  const [saved, setSaved] = useState<string[]>([]);

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return FEED_ITEMS.filter((it) => {
      const okCat = cat === '全部' || it.category === cat;
      const okQ =
        !q || [it.title, it.excerpt, it.tag, it.category].join(' ').toLowerCase().includes(q);
      return okCat && okQ;
    });
  }, [cat, query]);

  const toggle = (id: string) =>
    setSaved((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  return (
    <section id="feed" className="bg-background-100/60 py-14 md:py-20">
      <div className="mx-auto max-w-6xl px-4 md:px-6 lg:px-8">
        <SectionHeading
          eyebrow="最新热瓜"
          title={query.trim() ? `“${query.trim()}” 相关热瓜` : '热门爆料实时滚动'}
          desc="按分类与关键词即时筛选，收藏感兴趣的瓜，回看更方便。所有内容均来自公开信源整理，持续跟进反转。"
        />

        {/* filter bar */}
        <div className="mb-6 flex flex-col gap-3 md:mb-8 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-2 rounded-full bg-background-200/60 p-1 w-fit">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCat(c)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  cat === c
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 text-sm text-foreground-500">
            <span>
              共 <strong className="text-primary-600">{list.length}</strong> 条
            </span>
            {saved.length > 0 && (
              <span className="inline-flex items-center gap-1 text-accent-700">
                <i className="ri-bookmark-fill"></i>已收藏 {saved.length}
              </span>
            )}
          </div>
        </div>

        {/* grid */}
        {list.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-background-300 bg-background-50 py-20 text-center">
            <i className="ri-inbox-archive-line text-4xl text-foreground-300"></i>
            <p className="mt-4 text-base font-medium text-foreground-700">这口瓜还没熟透</p>
            <p className="mt-1 text-sm text-foreground-500">
              换个关键词或分类再试试，或去「今日热榜」看看正在发酵的话题。
            </p>
            <a
              href="#hot"
              className="mt-5 inline-flex items-center whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-opacity hover:opacity-90"
            >
              去看热榜
            </a>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {list.map((it) => {
              const isSaved = saved.includes(it.id);
              return (
                <article
                  key={it.id}
                  className="group flex flex-col overflow-hidden rounded-2xl border border-background-200/70 bg-background-50 transition-transform duration-300 hover:-translate-y-1"
                >
                  <div className="relative aspect-[4/5] overflow-hidden">
                    <img
                      src={it.image}
                      alt={`${it.title}配图`}
                      title={`${it.title} · 吃瓜八卦`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <span
                      className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-xs font-semibold text-background-50 ${
                        CAT_COLORS[it.category] || 'bg-foreground-600'
                      }`}
                    >
                      {it.category}
                    </span>
                    <button
                      type="button"
                      onClick={() => toggle(it.id)}
                      aria-label={isSaved ? '取消收藏' : '收藏此瓜'}
                      aria-pressed={isSaved}
                      className={`absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full backdrop-blur-md transition-colors ${
                        isSaved
                          ? 'bg-accent-500 text-background-50'
                          : 'bg-foreground-950/35 text-background-50 hover:bg-foreground-950/55'
                      }`}
                    >
                      <i className={`${isSaved ? 'ri-bookmark-fill' : 'ri-bookmark-line'} text-base`}></i>
                    </button>
                  </div>
                  <div className="flex flex-1 flex-col p-4 md:p-5">
                    <div className="mb-2 flex items-center gap-2">
                      <span className="rounded-full bg-primary-100 px-2 py-0.5 font-label text-[11px] font-medium text-primary-700">
                        #{it.tag}
                      </span>
                      <span className="font-label text-[11px] text-foreground-400">{it.time}</span>
                    </div>
                    <h3 className="line-clamp-2 font-heading text-base font-bold leading-snug text-foreground-950">
                      {it.title}
                    </h3>
                    <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-foreground-600">
                      {it.excerpt}
                    </p>
                    <div className="mt-4 flex items-center justify-between border-t border-background-100 pt-3 text-xs text-foreground-400">
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-fire-line text-primary-500"></i>
                        {heatText(it.heat)} 围观
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {it.read}
                      </span>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}